import { useCallback, useEffect, useRef, useState } from 'react';
import { useDataVersion } from './useDataVersion';

type PollState<T> = {
  data: T | null;
  error: string | null;
  loading: boolean;
  refresh: () => void;
};

// The MVP has no WebSocket: the floor plan and the live board stay current by
// re-fetching on an interval. The first load shows a spinner; every refresh
// after that swaps the data in silently so the screen never flickers while
// staff are looking at it. A write anywhere in the app (see `useDataVersion`)
// also refetches immediately, so an action taken on one part of the screen is
// reflected everywhere else without waiting out the interval.
//
// Polling only runs while the tab is actually on screen. A dashboard left
// open in a background tab, a minimised window or a phone in a pocket would
// otherwise keep hitting the backend every few seconds for nobody — and with
// three pollers per page, every stray tab on any staff device adds well over
// half a request a second. When the tab comes back it fetches straight away,
// so the numbers are never staler than a fresh load.
export function usePoll<T>(
  fetcher: (signal: AbortSignal) => Promise<T>,
  intervalMs: number,
  deps: unknown[] = [],
): PollState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);
  const version = useDataVersion();

  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;
    let inFlight = false;
    let timer: number | null = null;

    const run = async () => {
      // A slow backend must not have ticks piling up behind each other.
      if (inFlight) return;
      inFlight = true;
      try {
        const result = await fetcherRef.current(controller.signal);
        if (cancelled) return;
        setData(result);
        setError(null);
      } catch (err) {
        if (cancelled || (err as Error).name === 'AbortError') return;
        setError((err as Error).message);
      } finally {
        inFlight = false;
        if (!cancelled) setLoading(false);
      }
    };

    const start = () => {
      if (timer !== null) return;
      void run();
      timer = window.setInterval(run, intervalMs);
    };
    const stop = () => {
      if (timer === null) return;
      window.clearInterval(timer);
      timer = null;
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') start();
      else stop();
    };

    onVisibility();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      controller.abort();
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs, nonce, version, ...deps]);

  return { data, error, loading, refresh };
}
